import { supabase } from './supabase';
import { fetchWithTimeout } from './fetchWithTimeout';

export interface PersonalizedSummary {
  id: string;
  user_id: string;
  docket_number: string;
  topics: string[];
  topics_hash: string;
  summary_type: 'multi-topic' | 'single-topic';
  filing_type?: string;
  period_type?: 'comments' | 'reply_comments';
  storage_path: string;
  file_size?: number;
  document_count: number;
  processing_time_ms?: number;
  created_at: string;
  updated_at: string;
  /** Decoupled executive-summary (refinable). Null for legacy rows. */
  exec_length?: 'brief' | 'detailed' | null;
  exec_context?: string | null;
  exec_generated_at?: string | null;
  exec_storage_path?: string | null;
  topic_summaries_storage_path?: string | null;
  /** URL-safe token used by /s/:token to read the summary anonymously. */
  share_token?: string | null;
  /** Owner can flip OFF to revoke the share link. */
  is_shareable?: boolean | null;
}

export interface ExecSummaryPayload {
  summary_id: string;
  content: string;
  length: 'brief' | 'detailed';
  context: string | null;
  generated_at: string;
}

export interface RefineExecSummaryRequest {
  length: 'brief' | 'detailed';
  context?: string;
}

export interface TopicSectionPayload {
  topic_name: string;
  slug: string;
  /** 'slim' = bullet-list fallback rendered from the slim topic_summaries.json
   *  (no LLM call yet). 'refined' = LLM-generated narrative markdown. */
  source: 'slim' | 'refined';
  length: 'brief' | 'detailed' | null;
  context: string | null;
  focus_stakeholders: string[] | null;
  generated_at: string | null;
  markdown: string;
  /** Filers contributing to this topic — present on 'slim' entries so the
   *  Refine UI can offer a stakeholder multi-select. */
  filers?: string[];
}

export interface RefineTopicSectionRequest {
  topic_name: string;
  length: 'brief' | 'detailed';
  context?: string;
  focus_stakeholders?: string[];
}

export interface PersonalizedSummaryRequest {
  docketNumber: string;
  topics: string[];
  filingType?: string | string[];
  /** Optional list of storage-folder segments to retrieve from. When
   *  not provided, the backend defaults to filingType. Used to expand
   *  the search when mis-classified non-comment filings are opted in. */
  filingTypes?: string[];
  periodType?: 'comments' | 'reply_comments';
  summaryType: 'multi-topic' | 'single-topic';
  maxDocsPerTopic?: number;
  detailLevel?: 'concise' | 'standard' | 'detailed';
  forceRefresh?: boolean;
  selectedComments?: string[];
  nprm_filename?: string;
  public_notice_filename?: string;
  relevanceThreshold?: number;
  similarityThreshold?: number;
  /** Flat list of all submission IDs from selected relevant stakeholders. */
  relevant_stakeholder_ids?: string[];
  /** Display names of selected relevant stakeholders. */
  relevant_stakeholder_names?: string[];
  /** Optional user-supplied steering text for the executive summary.
   *  Threaded into the detailed-exec prompt server-side. */
  execFocus?: string;
}

export interface PersonalizedSummaryResponse {
  summary: PersonalizedSummary;
  content: string;
  topicSummaries?: Array<{
    topic: string;
    summary: string;
    documentCount: number;
  }>;
  overallSummary?: string;
}

export interface PersonalizedSummaryJob {
  id: string;
  user_id: string;
  docket_number: string;
  topics: string[];
  topics_hash: string;
  summary_type: 'multi-topic' | 'single-topic';
  filing_type?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  result_summary_id?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface JobStartResponse {
  status: 'started' | 'in_progress';
  message: string;
  jobId: string;
  progress: number;
  estimatedTimeMinutes: number;
}

export class PersonalizedSummaryService {
  private static instance: PersonalizedSummaryService;

  static getInstance(): PersonalizedSummaryService {
    if (!PersonalizedSummaryService.instance) {
      PersonalizedSummaryService.instance = new PersonalizedSummaryService();
    }
    return PersonalizedSummaryService.instance;
  }

  /**
   * Generate a personalized summary for the current user (async without polling)
   * Starts the job and returns immediately. User will be emailed when complete.
   */
  async generatePersonalizedSummary(
    request: PersonalizedSummaryRequest
  ): Promise<{ jobId: string; message: string }> {
    // Use getSession() rather than getUser() — getSession reads from local
    // storage and doesn't take the supabase-js auth lock (which can wedge
    // for up to 15s if another tab holds it). We just need user.id to
    // confirm the user is signed in; the edge function re-verifies the
    // token server-side.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('User must be authenticated to generate personalized summaries');
    }

    // No tier check here — callers (SummariesSection.generatePeriodSummary,
    // MultiTopicSummaryModal) already call userTierService.canPerformAction
    // before invoking this method. Doing it again here added a redundant
    // round-trip to the backend's /subscription/status endpoint, costing
    // ~1-3 s of extra latency on every Generate click. The backend's own
    // check_summary_limits inside /create_personalized_summary is still
    // the authoritative gate — if a request gets through here, it can
    // still be rejected at the backend with a 403 surfaced via the job's
    // error_message.

    // Skip the frontend's getPersonalizedSummary dedup check. The edge
    // function does the same lookup, with the same topics_hash, against
    // the same table — and is the source of truth either way. Calling it
    // here too costs an extra round-trip, two .single() 406 responses
    // (one for each fallback path) and one auth-lock wait, all on the
    // critical click path.

    try {
      // Start the job (returns immediately).
      const jobResponse = await this.startPersonalizedSummaryJob(request);

      return {
        jobId: jobResponse.jobId,
        message: 'Your personalized summary is being generated. You will receive an email when it\'s ready. You can also refresh this page later to view it.'
      };

    } catch (error) {
      console.error('Error starting personalized summary job:', error);
      throw error;
    }
  }

  /**
   * Start a personalized summary job
   */
  async startPersonalizedSummaryJob(request: PersonalizedSummaryRequest): Promise<JobStartResponse> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('User must be authenticated to generate personalized summaries');
    }

    const {
      docketNumber,
      topics,
      filingType,
      filingTypes,
      periodType,
      summaryType,
      maxDocsPerTopic = 100,
      detailLevel = 'concise',
      forceRefresh = false,
      selectedComments = [],
      nprm_filename = undefined,
      public_notice_filename = undefined,
      relevanceThreshold = 0.5,
      similarityThreshold = 0.6,
      relevant_stakeholder_ids,
      relevant_stakeholder_names,
      execFocus,
    } = request;

    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-personalized-summary`;
    
    const requestBody: any = {
      docketNumber,
      topics,
      summaryType,
      maxDocsPerTopic,
      detailLevel,
      userToken: session.access_token,
      forceRefresh,
      relevanceThreshold,
      similarityThreshold
    };

    if (filingType) {
      requestBody.filingType = filingType;
    }

    if (filingTypes && filingTypes.length > 0) {
      requestBody.filingTypes = filingTypes;
    }

    if (periodType) {
      requestBody.periodType = periodType;
    }

    if (selectedComments && selectedComments.length > 0) {
      requestBody.selectedComments = selectedComments;
    }

    if (nprm_filename) {
      requestBody.nprm_filename = nprm_filename;
    }

    if (public_notice_filename) {
      requestBody.public_notice_filename = public_notice_filename;
    }

    if (relevant_stakeholder_ids && relevant_stakeholder_ids.length > 0) {
      requestBody.relevant_stakeholder_ids = relevant_stakeholder_ids;
    }

    if (relevant_stakeholder_names && relevant_stakeholder_names.length > 0) {
      requestBody.relevant_stakeholder_names = relevant_stakeholder_names;
    }

    if (execFocus && execFocus.trim()) {
      requestBody.execFocus = execFocus.trim();
    }

    const response = await fetchWithTimeout(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle billing limit errors (403)
      if (response.status === 403 && errorData.upgrade_required) {
        const error = new Error(errorData.error || 'Usage limit reached') as any;
        error.upgradeRequired = true;
        error.usage = errorData.usage;
        throw error;
      }
      
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData = await response.json();

    // A cache hit means the summary already exists — there's no job to poll.
    // Signal that to the caller with a sentinel jobId it already handles
    // (`response.jobId === 'cached'` → reload existing summaries). Previously
    // this THREW Error('CACHED_RESPONSE'), which the caller's cached-branch
    // never caught, so the throw landed in the generic catch and rendered a
    // raw red "CACHED_RESPONSE" error instead of the summary.
    if (responseData.cached) {
      return { ...responseData, jobId: 'cached', cached: true } as JobStartResponse;
    }

    if (responseData.error) {
      throw new Error(responseData.error);
    }

    return responseData as JobStartResponse;
  }

  /**
   * Poll job status until completion
   */
  async pollJobStatus(
    jobId: string, 
    onProgress?: (progress: number, status: string) => void
  ): Promise<PersonalizedSummaryJob> {
    const maxAttempts = 540; // 45 minutes max (540 * 5 seconds)
    const pollInterval = 5000; // 5 seconds
    let attempts = 0;

    while (attempts < maxAttempts) {
      const { data: job, error } = await supabase
        .from('personalized_summary_jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch job status: ${error.message}`);
      }

      if (!job) {
        throw new Error('Job not found');
      }

      const typedJob = job as PersonalizedSummaryJob;
      
      onProgress?.(typedJob.progress, typedJob.status);

      if (typedJob.status === 'completed') {
        return typedJob;
      }

      if (typedJob.status === 'failed') {
        throw new Error(typedJob.error_message || 'Job failed');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;
    }

    throw new Error('Job polling timed out after 45 minutes');
  }

  /**
   * Get job status (for manual checking)
   */
  async getJobStatus(jobId: string): Promise<PersonalizedSummaryJob> {
    const { data: job, error } = await supabase
      .from('personalized_summary_jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (error) {
      throw new Error(`Failed to fetch job status: ${error.message}`);
    }

    if (!job) {
      throw new Error('Job not found');
    }

    return job as PersonalizedSummaryJob;
  }

  /**
   * Get existing personalized summary for user
   * For reply comment period summaries, doesn't require user_id match (shared summaries)
   */
  async getPersonalizedSummary(userId: string, docketNumber: string, topicsHash: string, filingType?: string, periodType?: 'comments' | 'reply_comments'): Promise<PersonalizedSummary | null> {
    let query = supabase
      .from('personalized_summaries')
      .select('*')
      .eq('docket_number', docketNumber)
      .eq('topics_hash', topicsHash);
    
    // Try to use period_type for filtering if available, otherwise fall back to filing_type
    if (periodType) {
      try {
        query = query.eq('period_type', periodType);
        if (periodType === 'reply_comments') {
          // Reply comment period summaries are shared, don't filter by user_id
        } else {
          query = query.eq('user_id', userId);
        }
        
        const { data, error } = await query.maybeSingle();

        // If period_type column doesn't exist (406 error), fall back to filing_type
        if (error && (error.code === 'PGRST116' || error.message?.includes('period_type') || error.message?.includes('406'))) {
          // Fall back to filing_type based filtering
          query = supabase
            .from('personalized_summaries')
            .select('*')
            .eq('docket_number', docketNumber)
            .eq('topics_hash', topicsHash);
          
          if (periodType === 'reply_comments' || filingType === 'REPLY_COMM') {
            query = query.eq('filing_type', 'REPLY_COMM');
          } else {
            query = query.eq('user_id', userId);
          }
          
          const { data: fallbackData, error: fallbackError } = await query.maybeSingle();
          if (fallbackError || !fallbackData) {
            return null;
          }
          return fallbackData as PersonalizedSummary;
        }
        
        if (error || !data) {
          return null;
        }
        
        return data as PersonalizedSummary;
      } catch (err) {
        // If period_type column doesn't exist, fall back to filing_type
        query = supabase
          .from('personalized_summaries')
          .select('*')
          .eq('docket_number', docketNumber)
          .eq('topics_hash', topicsHash);
        
        if (periodType === 'reply_comments' || filingType === 'REPLY_COMM') {
          query = query.eq('filing_type', 'REPLY_COMM');
        } else {
          query = query.eq('user_id', userId);
        }
        
        const { data: fallbackData, error: fallbackError } = await query.maybeSingle();
        if (fallbackError || !fallbackData) {
          return null;
        }
        return fallbackData as PersonalizedSummary;
      }
    } else if (filingType === 'REPLY_COMM') {
      query = query.eq('filing_type', 'REPLY_COMM');
    } else {
      query = query.eq('user_id', userId);
    }
    
    const { data, error } = await query.maybeSingle();

    if (error || !data) {
      return null;
    }

    return data as PersonalizedSummary;
  }

  /**
   * Get all personalized summaries for a user and docket
   * Includes both user-specific summaries and shared reply comment period summaries
   */
  async getUserPersonalizedSummaries(docketNumber: string): Promise<PersonalizedSummary[]> {
    // Read path — use getSession (local storage) instead of getUser
    // (network + auth lock). On the SummariesSection mount path this
    // saves ~hundreds-of-ms per visit; the actual data fetch below is
    // gated by row-level security on user_id, so a slightly-stale
    // session can't leak data.
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) {
      // Anonymous visitor has no saved summaries — return empty rather than
      // throwing, so browsing the Summaries tab logged-out doesn't spew
      // "User must be authenticated" console errors.
      return [];
    }

    // Normalize docket number for case-insensitive matching
    const normalizedDocket = docketNumber.trim().toUpperCase().replace(/\s+/g, '');

    // Get all user summaries for this docket first
    // Use case-insensitive matching by normalizing both sides
    // Note: We'll query and then filter client-side for case-insensitive match
    const { data: allUserSummaries, error: allError } = await supabase
      .from('personalized_summaries')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (allError) {
      throw new Error(`Failed to fetch personalized summaries: ${allError.message}`);
    }

    // Filter by normalized docket number (case-insensitive)
    const matchingSummaries = (allUserSummaries || []).filter(s => {
      const summaryDocket = s.docket_number?.trim().toUpperCase().replace(/\s+/g, '') || '';
      return summaryDocket === normalizedDocket;
    });

    // Return ALL matching summaries for the docket (not filtered by period_type)
    // This allows users to share any summary they've created, regardless of period type
    // Sort by created_at (most recent first)
    const allSummaries = matchingSummaries.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA; // Most recent first
    });

    return allSummaries as PersonalizedSummary[];
  }

  /**
   * Get all personalized summaries for the current user across all dockets
   */
  async getAllUserPersonalizedSummaries(): Promise<PersonalizedSummary[]> {
    // Read path — see comment on getUserPersonalizedSummaries.
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const { data, error } = await supabase
      .from('personalized_summaries')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch all personalized summaries: ${error.message}`);
    }

    return data as PersonalizedSummary[];
  }

  /**
   * Delete a personalized summary
   */
  async deletePersonalizedSummary(summaryId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Get the summary to get the storage path
    const summary = await this.getPersonalizedSummaryById(summaryId);
    if (!summary || summary.user_id !== user.id) {
      throw new Error('Summary not found or access denied');
    }

    // Delete from storage
    await supabase.storage
      .from('documents')
      .remove([summary.storage_path]);

    // Delete from database
    const { error } = await supabase
      .from('personalized_summaries')
      .delete()
      .eq('id', summaryId)
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Failed to delete personalized summary: ${error.message}`);
    }
  }

  /**
   * Generate hash for topics array
   */
  private async generateTopicsHash(topics: string[]): Promise<string> {
    // Sort topics for consistent hashing
    const sortedTopics = [...topics].sort();
    const topicsString = sortedTopics.join('|');
    
    // Use Web Crypto API for hashing
    const encoder = new TextEncoder();
    const data = encoder.encode(topicsString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Validate storage path format
   */
  private validateStoragePath(storagePath: string): boolean {
    // Pattern for comment period summaries: summaries/{docket}/personalized/{userId}/COMMENT/{hash}
    const commentPattern = /^summaries\/[^\/]+\/personalized\/[^\/]+\/COMMENT\/[a-f0-9]+$/i;
    // Pattern for reply comment period summaries: ecfs/{docket}/personalized/REPLY_COMM/{hash}
    const replyCommPattern = /^ecfs\/[^\/]+\/personalized\/REPLY_COMM\/[a-f0-9]+$/i;
    // Legacy pattern for backward compatibility: summaries/{docket}/personalized/{userId}/{hash}
    const legacyPattern = /^summaries\/[^\/]+\/personalized\/[^\/]+\/[a-f0-9]+$/i;
    // Legacy pattern with filing_type in path (old format): summaries/{docket}/personalized/{userId}/{filing_type}/{hash}
    const legacyWithFilingTypePattern = /^summaries\/[^\/]+\/personalized\/[^\/]+\/[^\/]+\/[a-f0-9]+$/i;
    return commentPattern.test(storagePath) || replyCommPattern.test(storagePath) || legacyPattern.test(storagePath) || legacyWithFilingTypePattern.test(storagePath);
  }

  /**
   * Create storage path with validation
   */
  private createStoragePath(userId: string, docketNumber: string, topicsHash: string): string {
    // Validate inputs
    if (!userId || !docketNumber || !topicsHash) {
      throw new Error('Invalid parameters for storage path creation');
    }
    
    // Ensure docket number is sanitized (remove any potentially dangerous characters)
    const sanitizedDocketNumber = docketNumber.replace(/[^a-zA-Z0-9\-_\.]/g, '');
    
    // Ensure topics hash is valid hex string
    if (!/^[a-f0-9]+$/i.test(topicsHash)) {
      throw new Error('Invalid topics hash format');
    }
    
    // Create the personalized storage path
    const storagePath = `summaries/${sanitizedDocketNumber}/personalized/${userId}/${topicsHash}`;
    
    // Validate the final path
    if (!this.validateStoragePath(storagePath)) {
      throw new Error('Invalid storage path format');
    }
    
    return storagePath;
  }

  /**
   * Store personalized summary content
   */
  private async storePersonalizedSummary(storagePath: string, content: string): Promise<void> {
    // Validate storage path before attempting to upload
    if (!this.validateStoragePath(storagePath)) {
      throw new Error(`Invalid storage path format: ${storagePath}`);
    }

    const { error } = await supabase.storage
      .from('documents')
      .upload(`${storagePath}/summary.txt`, new Blob([content], { type: 'text/plain' }));

    if (error) {
      throw new Error(`Failed to store personalized summary: ${error.message}`);
    }
  }

  /**
   * Get summary content from storage
   */
  private async getSummaryContent(storagePath: string): Promise<string> {
    // Validate storage path before attempting to download
    if (!this.validateStoragePath(storagePath)) {
      throw new Error(`Invalid storage path format: ${storagePath}`);
    }

    const { data, error } = await supabase.storage
      .from('documents')
      .download(`${storagePath}/summary.txt`);

    if (error || !data) {
      throw new Error(`Failed to retrieve summary content: ${error?.message}`);
    }

    return await data.text();
  }

  /**
   * Create personalized summary database record
   */
  private async createPersonalizedSummaryRecord(summaryData: Partial<PersonalizedSummary>): Promise<PersonalizedSummary> {
    const { data, error } = await supabase
      .from('personalized_summaries')
      .insert(summaryData)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create personalized summary record: ${error.message}`);
    }

    return data as PersonalizedSummary;
  }

  /**
   * Get personalized summary by ID
   */
  async getPersonalizedSummaryById(summaryId: string): Promise<PersonalizedSummary | null> {
    const { data, error } = await supabase
      .from('personalized_summaries')
      .select('*')
      .eq('id', summaryId)
      .single();

    if (error || !data) {
      return null;
    }

    return data as PersonalizedSummary;
  }

  /**
   * Extract document count from summary content
   */
  private extractDocumentCount(content: string): number {
    // Look for patterns like "X documents" or "X files" in the content
    const matches = content.match(/(\d+)\s+(documents?|files?)/i);
    return matches ? parseInt(matches[1]) : 0;
  }

  /**
   * Parse topic summaries from content (placeholder implementation)
   */
  private async parseTopicSummaries(content: string): Promise<Array<{ topic: string; summary: string; documentCount: number }>> {
    // This would need to be implemented based on the actual format of the summary content
    // For now, return empty array
    return [];
  }

  /**
   * Parse overall summary from content (placeholder implementation)
   */
  private async parseOverallSummary(content: string): Promise<string> {
    // This would need to be implemented based on the actual format of the summary content
    // For now, return the full content
    return content;
  }

  /**
   * Get topic summaries from storage (placeholder implementation)
   */
  private async getTopicSummaries(storagePath: string): Promise<Array<{ topic: string; summary: string; documentCount: number }>> {
    // This would need to be implemented based on how topic summaries are stored
    // For now, return empty array
    return [];
  }

  /**
   * Get overall summary from storage (placeholder implementation)
   */
  private async getOverallSummary(storagePath: string): Promise<string> {
    // This would need to be implemented based on how overall summaries are stored
    // For now, return empty string
    return '';
  }

  /**
   * Download summary content as a file (Word document)
   */
  async downloadSummaryContent(summary: PersonalizedSummary): Promise<void> {
    try {
      // Validate storage path before attempting to download
      if (!this.validateStoragePath(summary.storage_path)) {
        throw new Error(`Invalid storage path format: ${summary.storage_path}`);
      }

      // Download the .docx file from storage
      const { data, error } = await supabase.storage
        .from('documents')
        .download(`${summary.storage_path}/summary.docx`);

      if (error || !data) {
        throw new Error(`Failed to retrieve summary file: ${error?.message || 'File not found'}`);
      }

      // Create a filename based on docket number, topics, and date
      const date = new Date(summary.created_at).toISOString().split('T')[0];
      const topicsStr = summary.topics.join('_').replace(/[^a-zA-Z0-9_-]/g, '');
      const filename = `summary_${summary.docket_number}_${topicsStr}_${date}.docx`;
      
      // data from Supabase storage download is already a Blob
      // Create a download link and trigger download
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading summary:', error);
      throw new Error(`Failed to download summary: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // -------------------------------------------------------------------------
  // Executive-summary (decoupled, refinable)
  // -------------------------------------------------------------------------

  private getBackendBaseUrl(): string {
    const raw = (import.meta as { env?: { VITE_RAG_API_BASE_URL?: string } })?.env?.VITE_RAG_API_BASE_URL;
    const fallback = 'https://fcc-summarize-app-r4ac3.ondigitalocean.app';
    return (raw || fallback).replace(/\/+$/, '');
  }

  /** Fetch the current executive-summary markdown for a personalized
   *  summary. Returns null if the summary predates the refinable-exec
   *  feature (no exec.md persisted) — callers should disable Refine UI
   *  in that case. */
  async getExecSummary(summaryId: string): Promise<ExecSummaryPayload | null> {
    const url = `${this.getBackendBaseUrl()}/personalized_summary/${encodeURIComponent(summaryId)}/exec`;
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetchWithTimeout(url, { method: 'GET', headers: { 'Authorization': `Bearer ${session?.access_token ?? ''}` } });
    if (res.status === 404) return null;
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Failed to fetch exec summary (${res.status}): ${text.slice(0, 200)}`);
    }
    return (await res.json()) as ExecSummaryPayload;
  }

  /** Regenerate the executive summary with new length / context. Reads the
   *  persisted topic_summaries.json server-side, runs one LLM pass, and
   *  returns the new exec markdown. Cheap relative to full regeneration
   *  (~$0.01-0.05, a few seconds). */
  async refineExecSummary(
    summaryId: string,
    request: RefineExecSummaryRequest,
  ): Promise<ExecSummaryPayload> {
    const url = `${this.getBackendBaseUrl()}/personalized_summary/${encodeURIComponent(summaryId)}/exec`;
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token ?? ''}` },
      body: JSON.stringify({
        length: request.length,
        context: request.context ?? null,
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Failed to refine exec summary (${res.status}): ${text.slice(0, 200)}`);
    }
    return (await res.json()) as ExecSummaryPayload;
  }

  // -------------------------------------------------------------------------
  // Per-topic refine — same pattern, scoped to one topic of the summary
  // -------------------------------------------------------------------------

  /** Returns the current rendered markdown for every topic in this
   *  summary. Topics that have never been refined come back with
   *  `source: 'slim'` and a bullet-list fallback (no LLM call). */
  async getTopicSections(summaryId: string): Promise<TopicSectionPayload[]> {
    const url = `${this.getBackendBaseUrl()}/personalized_summary/${encodeURIComponent(summaryId)}/topic_sections`;
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetchWithTimeout(url, { method: 'GET', headers: { 'Authorization': `Bearer ${session?.access_token ?? ''}` } });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Failed to load topic sections (${res.status}): ${text.slice(0, 200)}`);
    }
    const data = (await res.json()) as { topics: TopicSectionPayload[] };
    return data.topics ?? [];
  }

  /** Regenerate a single topic section with new length / context /
   *  focus-stakeholder emphasis. Cheap; reads the slim topic slice
   *  server-side and runs one LLM call. */
  async refineTopicSection(
    summaryId: string,
    request: RefineTopicSectionRequest,
  ): Promise<TopicSectionPayload> {
    const url = `${this.getBackendBaseUrl()}/personalized_summary/${encodeURIComponent(summaryId)}/topic_section`;
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token ?? ''}` },
      body: JSON.stringify({
        topic_name: request.topic_name,
        length: request.length,
        context: request.context ?? null,
        focus_stakeholders: request.focus_stakeholders ?? null,
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Failed to refine topic section (${res.status}): ${text.slice(0, 200)}`);
    }
    return (await res.json()) as TopicSectionPayload;
  }
}

export const personalizedSummaryService = PersonalizedSummaryService.getInstance();

